import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'
import { useEffect, useState } from 'react'

import { invalidateVocabulary, vocabularyQuery } from '@/api/vocab'

import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { isOverlayActive, isTypingTarget, registerShortcuts, shortcutLetter } from '@/lib/keys'
import { useViewportFill, VIEWPORT_FIT_CLASS } from '@/lib/useViewportFill'
import { useUiStore } from '@/stores/uiStore'
import { useVocabStore } from '@/stores/vocabStore'
import { isStageBody, setEditorToastSink, useEditorStore, type EditorState } from '@/stores/editorStore'
import type { Pt } from '@/geo/types'

import { AnnotationCanvas } from './AnnotationCanvas'
import { AutoAnnotateButton } from './AutoAnnotateButton'
import { CanvasStatusBar } from './CanvasStatusBar'
import { FitPicture } from './FitPicture'
import { KindLegend } from './KindLegend'
import { KindDialogs } from './KindPicker'
import { LayerList } from './LayerList'
import { LintList } from './LintList'
import { Magnifier } from './Magnifier'
import { ProposalReviewBar } from './ProposalReviewBar'
import { PropertiesPanel } from './PropertiesPanel'
import { ReviewStatusPill } from './ReviewStatusPill'
import { SaveControls } from './SaveControls'
import { ShortcutSheet } from './ShortcutSheet'
import { StoryReturnBar } from './StoryReturnBar'
import { ToolRail } from './ToolRail'
import { TOOLS } from './tools/reducer'
import { UndoRedoButtons } from './UndoRedoButtons'
import { useEditorCommands } from './useEditorCommands'
import { VideoScrubber } from './VideoScrubber'
import { isFallbackVocab, useVocab } from './vocab'

/**
 * The expert annotator shell (§3.2): header (save, undo/redo, AI) · tool rail · canvas · kind bar
 * and side panels · status bar, plus the editor's shortcut scope (§9.3).
 *
 * Layout. The shell fills exactly the window between the top nav and the queue strip
 * (`useViewportFill` + `VIEWPORT_FIT_CLASS`) at every width; the page never scrolls, only the
 * side panels do, each inside itself. The picture sits in a 16:9 box (`FitPicture`) because the
 * canvas maths read the svg box as the picture. Below `lg` the tool rail becomes a row, the
 * picture keeps its 16:9 height, and the kind bar and panels share what is left under it — never
 * wider than the window. Only a window too short for the editor at all falls back to scrolling.
 *
 * Keys. Save / undo / redo / Esc / Enter live in `useEditorCommands` (shared with Simple mode);
 * the kind digits and `/` in the kind bar; the rest (tools, zoom, nudge, delete…) here, because
 * they must work while the focus is in the layer list or the details panel — the whole point of
 * those panels being an accessible mirror of the canvas. Letters are read with `shortcutLetter`,
 * so on a Russian (Cyrillic) layout the physical V key still picks the Select tool, while AZERTY
 * keeps its own letters.
 */

export interface AnnotatorLayoutProps {
  title: string
  plateUrl?: string
  video?: boolean
  videoDuration?: number
  returnTo?: string
  sceneId?: string
  onNavigate: (to: string) => void
  /** Leaving expert mode for the guided one. */
  onSwitchToSimple?: () => void
  onNextAsset?: () => void
  onPrevAsset?: () => void
}

/** Where Space belongs to the focused control (a button presses, a field types) rather than to panning. */
function spaceBelongsToTarget(e: KeyboardEvent): boolean {
  if (isTypingTarget(e)) return true
  const el = e.target
  if (!(el instanceof Element)) return false
  return el.closest('button, a[href], select, summary, [role="button"], [role="option"], [role="treeitem"], [role="checkbox"], [role="radio"], [role="tab"], [role="switch"], [role="menuitem"], [role="slider"]') !== null
}

const countProblems = (s: EditorState): number => (isStageBody(s.doc) ? s.doc.lints.filter((l) => l.severity !== 'info').length : 0)

/**
 * One tinykeys combo for every letter key: matched on `event.key` (a Latin letter) or on
 * `event.code` (`KeyV` — the physical key, which is all a Cyrillic layout has in common with the
 * letters). Which letter it means is decided by `shortcutLetter`, never by the combo: bound
 * letter-by-letter, tinykeys stops at the first match, so on a layout that moves letters around
 * a `KeyP` binding could swallow the press that types "l".
 */
export const ANY_LETTER = '([a-z]|Key[A-Z])'

/** Run the handler for the letter this press means; returns whether one ran (and stopped the browser's default). */
export function runLetter(handlers: Readonly<Record<string, () => void>>, e: KeyboardEvent): boolean {
  const letter = shortcutLetter(e)
  const fn = letter ? handlers[letter] : undefined
  if (!fn) return false
  e.preventDefault()
  fn()
  return true
}

/**
 * A punctuation shortcut (`[`, `]`, `` ` ``) by its character, or by its physical key when that
 * key types a letter of another alphabet there (Russian `х`, `ъ`, `ё`).
 */
export function isPunctuationKey(e: Pick<KeyboardEvent, 'key' | 'code'>, char: string, code: string): boolean {
  if (e.key === char) return true
  return e.code === code && e.key.length === 1 && /\p{L}/u.test(e.key) && !/^[a-z]$/i.test(e.key)
}

type KeyHandler = (e: KeyboardEvent) => void

/**
 * Editor keys do nothing while a menu or a dialog is open (or the key comes from inside one): the
 * arrows, Delete and the type-ahead letters belong to the menu then. Without this, ArrowDown to
 * the next item of a row's "…" menu also nudged the selected shape (an undo step and a human edit
 * nobody made), and Delete in a menu deleted the selection behind it.
 */
export function quietUnderOverlays(bindings: Readonly<Record<string, KeyHandler>>, opts: { preventDefault?: boolean } = {}): Record<string, KeyHandler> {
  const out: Record<string, KeyHandler> = {}
  for (const [combo, fn] of Object.entries(bindings)) {
    out[combo] = (e) => {
      // A focused control already used the key (the list's ArrowDown moved to the next row, the
      // tabs' arrows moved between tabs): it is not also a nudge of the picture.
      if (e.defaultPrevented || isOverlayActive(e)) return
      if (opts.preventDefault) e.preventDefault()
      fn(e)
    }
  }
  return out
}

/** Every feature of a stage document, for "select everything". */
function allFeatureIds(s: EditorState): string[] {
  const d = s.doc
  if (!isStageBody(d)) return []
  return [...d.regions, ...d.anchors, ...d.portals, ...d.paths, ...d.layers].map((f) => f.id)
}

export function AnnotatorLayout({
  title,
  plateUrl,
  video,
  videoDuration = 0,
  returnTo,
  sceneId,
  onNavigate,
  onSwitchToSimple,
  onNextAsset,
  onPrevAsset,
}: AnnotatorLayoutProps) {
  const vocab = useVocab()
  const fillRef = useViewportFill<HTMLDivElement>(true)
  useEditorCommands()
  const pushScope = useUiStore((s) => s.pushScope)
  const popScope = useUiStore((s) => s.popScope)

  const tool = useEditorStore((s) => s.tool)
  const meta = useEditorStore((s) => s.meta)
  const proposal = useEditorStore((s) => s.proposal)
  const selection = useEditorStore((s) => s.selection)
  const problems = useEditorStore(countProblems)
  const setTool = useEditorStore((s) => s.setTool)
  const setViewport = useEditorStore((s) => s.setViewport)
  const fit = useEditorStore((s) => s.fit)
  const deleteSelection = useEditorStore((s) => s.deleteSelection)
  const duplicateSelection = useEditorStore((s) => s.duplicateSelection)
  const nudgeSelection = useEditorStore((s) => s.nudgeSelection)
  const setProposalView = useEditorStore((s) => s.setProposalView)
  const setSolo = useEditorStore((s) => s.setSolo)
  const toggleVisible = useEditorStore((s) => s.toggleVisible)

  const [cursor, setCursor] = useState<Pt | null>(null)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [spaceHeld, setSpaceHeld] = useState(false)
  const [loupe, setLoupe] = useState(false)
  const [videoTime, setVideoTime] = useState(0)
  const [frameHeld, setFrameHeld] = useState(false)
  const [tab, setTab] = useState('layers')

  /* The list of kinds follows the server ---------------------------------- */
  // Every load and save tells us the server's vocabulary revision; when it is ahead of ours
  // (someone added or changed a kind elsewhere) fetch the list again, so new kinds just appear.
  const qc = useQueryClient()
  // Watch (never start) the app's own vocabulary request — the root route owns it — to tell
  // "still loading" (built-in kinds stand in silently) from "failed" (say so, offer a retry).
  const vocabQuery = useQuery({ ...vocabularyQuery({ includeDisabled: true }), enabled: false })
  const serverVocabRev = useEditorStore((s) => s.meta?.vocab_rev ?? 0)
  const vocabRev = useVocabStore((s) => s.rev)
  const vocabLoaded = useVocabStore((s) => s.loaded)
  useEffect(() => {
    if (vocabLoaded && serverVocabRev > vocabRev) void invalidateVocabulary(qc)
  }, [serverVocabRev, vocabRev, vocabLoaded, qc])

  /* Toasts from the tool machine ------------------------------------------ */
  useEffect(() => {
    setEditorToastSink((t) => {
      const fn = t.tone === 'error' ? toast.error : t.tone === 'warning' ? toast.warning : t.tone === 'success' ? toast.success : toast.info
      fn(t.message)
    })
    return () => setEditorToastSink(null)
  }, [])

  /* Shortcut scope --------------------------------------------------------- */
  useEffect(() => {
    pushScope('editor')
    return () => popScope('editor')
  }, [pushScope, popScope])

  useEffect(() => {
    const zoomIn = () => setViewport({ k: Math.min(16, useEditorStore.getState().viewport.k * 1.2) })
    const flipProposal = () => setProposalView(useEditorStore.getState().proposal?.view === 'off' ? 'overlay' : 'off')

    // Letters, by what they mean on any layout (see ANY_LETTER). M is the hold-to-look loupe, below.
    const plain: Record<string, () => void> = {}
    for (const t of TOOLS) if (t.shortcut !== 'm') plain[t.shortcut] = () => setTool(t.id)
    plain['e'] = () => {
      const id = useEditorStore.getState().selection.ids[0]
      if (id) toggleVisible(id)
    }
    const shifted: Record<string, () => void> = {
      e: () => {
        const id = useEditorStore.getState().selection.ids[0]
        if (id) setSolo(id)
      },
    }
    const withMod: Record<string, () => void> = {
      d: () => duplicateSelection(),
      a: () => useEditorStore.getState().select(allFeatureIds(useEditorStore.getState())),
    }
    const offLetters = registerShortcuts(
      'editor',
      quietUnderOverlays({
        [ANY_LETTER]: (e) => void runLetter(plain, e),
        [`Shift+${ANY_LETTER}`]: (e) => void runLetter(shifted, e),
        [`$mod+${ANY_LETTER}`]: (e) => void runLetter(withMod, e),
        // `[`, `]` and `` ` `` sit where Russian has х, ъ and ё: match the physical key there too.
        '(\\[|BracketLeft)': (e) => {
          if (!isPunctuationKey(e, '[', 'BracketLeft')) return
          e.preventDefault()
          onPrevAsset?.()
        },
        '(\\]|BracketRight)': (e) => {
          if (!isPunctuationKey(e, ']', 'BracketRight')) return
          e.preventDefault()
          onNextAsset?.()
        },
        '(`|Backquote)': (e) => {
          if (!isPunctuationKey(e, '`', 'Backquote')) return
          e.preventDefault()
          flipProposal()
        },
      }),
      // Only a press that means one of ours stops the browser's default (Ctrl+C, Ctrl+F… pass).
      { alwaysActive: false, preventDefault: false },
    )
    const offKeys = registerShortcuts(
      'editor',
      quietUnderOverlays({
        '$mod+0': () => fit(),
        '$mod+Digit0': () => fit(),
        '$mod+1': () => setViewport({ k: 1, tx: 0, ty: 0 }),
        '$mod+Digit1': () => setViewport({ k: 1, tx: 0, ty: 0 }),
        '+': zoomIn,
        'Shift++': zoomIn,
        '-': () => setViewport({ k: Math.max(0.1, useEditorStore.getState().viewport.k / 1.2) }),
        Delete: () => deleteSelection(),
        Backspace: () => deleteSelection(),
        // tinykeys matches modifiers exactly, so Shift+arrow (and Shift+? / Shift++) need their own entries.
        ArrowLeft: () => nudgeSelection(-0.1, 0),
        ArrowRight: () => nudgeSelection(0.1, 0),
        ArrowUp: () => nudgeSelection(0, -0.1),
        ArrowDown: () => nudgeSelection(0, 0.1),
        'Shift+ArrowLeft': () => nudgeSelection(-1, 0),
        'Shift+ArrowRight': () => nudgeSelection(1, 0),
        'Shift+ArrowUp': () => nudgeSelection(0, -1),
        'Shift+ArrowDown': () => nudgeSelection(0, 1),
        '?': () => setShortcutsOpen(true),
        'Shift+?': () => setShortcutsOpen(true),
      }, { preventDefault: true }),
      // The default is stopped by `quietUnderOverlays`, and only when the key is ours: inside an
      // open menu the arrows belong to the menu.
      { alwaysActive: false, preventDefault: false },
    )
    return () => {
      offLetters()
      offKeys()
    }
  }, [setTool, fit, setViewport, duplicateSelection, deleteSelection, nudgeSelection, setProposalView, toggleVisible, setSolo, onNextAsset, onPrevAsset])

  /* Space pans (and never scrolls the page); M holds the loupe ------------- */
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // A dialog (new kind, shortcuts) or an open list owns the keyboard.
      if (isOverlayActive(e)) return
      if (e.code === 'Space') {
        if (spaceBelongsToTarget(e)) return
        e.preventDefault()
        if (!e.repeat) setSpaceHeld(true)
        return
      }
      if (e.repeat || isTypingTarget(e)) return
      if (shortcutLetter(e) === 'm' && !e.ctrlKey && !e.metaKey && !e.altKey) setLoupe(true)
    }
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceHeld(false)
      if (shortcutLetter(e) === 'm') {
        setLoupe(false)
        if (useEditorStore.getState().tool === 'magnifier') setTool('select')
      }
    }
    const blur = () => {
      setSpaceHeld(false)
      setLoupe(false)
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      window.removeEventListener('blur', blur)
    }
  }, [setTool])

  return (
    <div ref={fillRef} className={cn('flex flex-col bg-paper lg:overflow-hidden', VIEWPORT_FIT_CLASS)} data-testid="annotator">
      {returnTo ? (
        <div className="shrink-0">
          <StoryReturnBar returnTo={returnTo} sceneId={sceneId} onNavigate={onNavigate} />
        </div>
      ) : null}

      {/* The built-in kinds stand in silently while the list loads; say so only when it failed. */}
      {isFallbackVocab(vocab) && vocabQuery.isError ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line bg-paper-2 px-4 py-1.5 text-xs text-ink-2" role="status" data-testid="fallback-vocab">
          <span className="min-w-0 flex-1">The list of kinds could not load, so only the studio’s basic kinds are offered for now.</span>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" loading={vocabQuery.isFetching} onClick={() => void vocabQuery.refetch()}>
            Try again
          </Button>
        </div>
      ) : null}

      {/*
        Below lg: the title and "…" (Simple mode, keyboard shortcuts) share the first line and the
        toolbar has the next one to itself, wrapping inside it. From lg: one line when it fits (the
        title keeps at least 10rem, else the toolbar moves under it). Nothing ever runs past the edge.
      */}
      <header className="flex min-w-0 shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-line px-3 py-1.5 sm:px-4">
        <h1 className="min-w-0 flex-1 basis-40 truncate text-sm font-semibold text-ink" title={title}>
          {title}
        </h1>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="w-9 shrink-0 px-0 lg:hidden" aria-label="More">
              <MoreHorizontal aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          {/* The shortcut sheet takes the focus: the closing menu must not pull it back to "…". */}
          <DropdownMenuContent align="end" data-testid="header-more" onCloseAutoFocus={(e) => e.preventDefault()}>
            {onSwitchToSimple ? <DropdownMenuItem onSelect={onSwitchToSimple}>Simple mode</DropdownMenuItem> : null}
            <DropdownMenuItem onSelect={() => setShortcutsOpen(true)}>Keyboard shortcuts</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex min-w-0 max-w-full basis-full flex-wrap items-center gap-1 lg:basis-auto" data-testid="annotator-toolbar">
          <UndoRedoButtons />
          {/* `contents`: its words and buttons wrap one by one with the rest of the toolbar. */}
          <SaveControls className="contents" />
          <AutoAnnotateButton />
          <ReviewStatusPill />
          {onSwitchToSimple ? (
            <Button variant="ghost" size="sm" className="hidden lg:inline-flex" onClick={onSwitchToSimple}>
              Simple mode
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" className="hidden w-9 px-0 lg:inline-flex" onClick={() => setShortcutsOpen(true)} aria-label="Keyboard shortcuts">
            ?
          </Button>
        </div>
      </header>

      {proposal ? (
        // Capped, so a long list of suggestions scrolls inside the bar instead of squeezing the picture.
        <div className="max-h-[40%] shrink-0 overflow-y-auto overscroll-contain" data-testid="proposal-bar-wrap">
          <ProposalReviewBar onFixOnPicture={onSwitchToSimple} onNext={onNextAsset} />
        </div>
      ) : null}

      {/* Below lg: tool row · picture · panels (the panels take what is left and scroll inside). */}
      <div
        className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[auto_auto_minmax(12rem,1fr)] lg:grid-cols-[auto_minmax(0,1fr)_300px] lg:grid-rows-[minmax(0,1fr)] xl:grid-cols-[auto_minmax(0,1fr)_340px]"
        data-testid="annotator-grid"
      >
        <ToolRail />

        <div className="relative flex min-h-0 min-w-0 flex-col">
          {/* 16:9 on its own below lg; from lg the well takes the row and FitPicture keeps the picture 16:9 inside it. */}
          <div className="relative aspect-video min-h-0 w-full min-w-0 overflow-hidden bg-paper-3 lg:aspect-auto lg:flex-1" data-testid="canvas-well">
            <FitPicture>
              <AnnotationCanvas
                plateUrl={plateUrl}
                video={video}
                videoTime={frameHeld ? videoTime : undefined}
                panArmed={spaceHeld}
                onCursor={setCursor}
              />
            </FitPicture>
            {loupe || tool === 'magnifier' ? <Magnifier src={plateUrl} at={cursor} /> : null}
          </div>
          {video && videoDuration > 0 ? (
            <div className="shrink-0">
              <VideoScrubber duration={videoDuration} time={videoTime} onTimeChange={setVideoTime} held={frameHeld} onHeldChange={setFrameHeld} />
            </div>
          ) : null}
          <CanvasStatusBar cursor={cursor} />
        </div>

        <aside className="flex min-h-0 min-w-0 flex-col border-t border-line lg:border-l lg:border-t-0" aria-label="Details">
          <KindLegend className="max-h-[35%] shrink-0 overflow-y-auto overscroll-contain border-b border-line p-3 lg:max-h-[45%]" />
          <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col" data-testid="side-tabs">
            <TabsList className="mx-2 mt-2 grid shrink-0 grid-cols-3">
              <TabsTrigger value="layers" className="min-w-0 px-1">
                Everything
              </TabsTrigger>
              <TabsTrigger value="props" className="min-w-0 px-1">
                Details{selection.ids.length > 1 ? ` (${selection.ids.length})` : ''}
              </TabsTrigger>
              <TabsTrigger value="lints" className="min-w-0 px-1">
                Check{problems ? ` (${problems})` : ''}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="layers" className="mt-1 min-h-0 flex-1">
              <LayerList />
            </TabsContent>
            <TabsContent value="props" className="mt-1 min-h-0 flex-1">
              <div className="h-full overflow-y-auto overscroll-contain" data-testid="details-scroll">
                <PropertiesPanel />
              </div>
            </TabsContent>
            <TabsContent value="lints" className="mt-1 min-h-0 flex-1">
              <div className="h-full overflow-y-auto overscroll-contain">
                <LintList />
              </div>
            </TabsContent>
          </Tabs>
          {meta ? (
            // Not on a phone: the room goes to the list instead.
            <footer className="hidden shrink-0 truncate border-t border-line px-3 py-1.5 text-[11px] text-ink-3 lg:block">
              <span className="font-mono">{meta.uid}</span> · version {meta.rev}
            </footer>
          ) : null}
        </aside>
      </div>

      <ShortcutSheet open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <KindDialogs />
    </div>
  )
}
